/**
 * Super Admin — AI Safety Review API module.
 * Base: /admin/ai-safety-review
 */
import { fetchApi } from '../client';

const BASE = '/api/admin/ai-safety-review';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AiReviewSummary {
  totalRequests: number;
  blockedByRules: number;
  errorEvents: number;
  criticalRiskCount: number;
  highRiskCount: number;
  pendingDoctorApprovals: number;
  approvedApprovals: number;
  rejectedApprovals: number;
  activeClinicsPast30Days: number;
  tokensPast30Days: number;
  pendingMappingReviews: number;
}

export interface AiRequestLogRow {
  id: string;
  clinicId: string;
  patientRef: string;
  prescriptionId: string | null;
  featureKey: string;
  requestStatus: string;
  ruleEngineRisk: string | null;
  aiRisk: string | null;
  finalRisk: string | null;
  blockedByRule: boolean;
  blockReason: string | null;
  cacheHit: boolean;
  latencyMs: number | null;
  totalTokens: number | null;
  estimatedCostUsd: number | null;
  doctorApprovalRequired: boolean;
  errorMessage: string | null;
  internalNote: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  markedForInvestigation: boolean;
  investigationNote: string | null;
  createdAt: string;
}

export interface AiFeatureConfigRow {
  id: string;
  clinicId: string;
  featureKey: string;
  enabled: boolean;
  maxRequestsPerMonth: number;
  maxTokensPerRequest: number;
  requireDoctorApprovalForHigh: boolean;
  requireDoctorApprovalForCritical: boolean;
  cacheEnabled: boolean;
  ragEnabled: boolean;
  safetyLevel: string;
  disableReason: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  createdAt: string | null;
}

export interface AiModelConfigRow {
  id: string;
  clinicId: string | null;
  featureKey: string;
  inferenceProfileArn: string;
  modelId: string;
  temperature: number;
  maxTokens: number;
  fallbackProfileArn: string | null;
  disableReason: string | null;
  isActive: boolean;
  updatedAt: string | null;
}

export interface AiPromptTemplateRow {
  id: string;
  featureKey: string;
  version: string;
  /** First 200 characters of the system prompt only — never save this as a prompt. */
  systemPromptPreview: string;
  /** Full texts, if the server sends them (it does not yet — see doc follow-up #2). */
  systemPrompt?: string | null;
  userPrompt?: string | null;
  effectiveFrom: string;
  isActive: boolean;
  promptHash: string | null;
  createdBy: string | null;
  deactivatedBy: string | null;
  deactivatedAt: string | null;
  activationNote: string | null;
  createdAt: string;
}

export interface AiUsageSummaryRow {
  id: string;
  clinicId: string;
  featureKey: string;
  yearMonth: string;
  totalRequests: number;
  successfulRequests: number;
  cacheHits: number;
  ruleBlocks: number;
  totalTokensUsed: number;
  estimatedCostUsd: number;
  quotaLimit: number;
  quotaExhausted: boolean;
  updatedAt: string | null;
}

export interface AiDoctorApprovalRow {
  id: string;
  clinicId: string;
  prescriptionId: string;
  patientRef: string;
  doctorId: string;
  riskLevel: string;
  approvalStatus: string;
  doctorComments: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  isStale: boolean;
  staleReason: string | null;
  markedStaleAt: string | null;
  createdAt: string;
}

export interface AiMappingRow {
  id: string;
  mappingType: 'ALLERGY' | 'MEDICINE' | 'DDI' | 'CONDITION_CAUTION' | 'DRUG_REQUEST';
  canonicalName: string;
  drugClassId: string | null;
  drugClassName: string | null;
  isHighRisk: boolean;
  reviewStatus: string;
  usedByRuleEngine: boolean;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  source: string | null;
  createdAt: string;
  // Populated when mapping was linked to medications_master
  medicationMasterId?: string | null;
  genericName?: string | null;
  strength?: string | null;
  dosageForm?: string | null;
  manufacturer?: string | null;
  contraindications?: string | null;
  drugInteractions?: string | null;
  // Populated for DDI / CONDITION_CAUTION mappings (AI-ingested candidates)
  severity?: string | null;
  managementNote?: string | null;
  sourceRef?: string | null;
  aiModelVersion?: string | null;
  rawSourceExcerpt?: string | null;
  ingestionRunId?: string | null;
  ingredientA?: string | null;
  ingredientB?: string | null;
  conditionKeyword?: string | null;
  drugIngredient?: string | null;
  // Drug request tracking
  reasonForAdding?: string | null;
  requestedBy?: string | null;
  wasDuplicate?: boolean;
}

export interface IngestionRunRow {
  id: string;
  runType: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  triggeredBy: string | null;
  startedAt: string;
  completedAt: string | null;
  totalItems: number;
  succeeded: number;
  failed: number;
  pending: number;
  candidatesGenerated: number;
  estimatedCost: number | null;
  errorMessage: string | null;
}

export interface AiErrorEventRow {
  id: string;
  clinicId: string;
  featureKey: string;
  requestStatus: string;
  finalRisk: string | null;
  blockedByRule: boolean;
  blockReason: string | null;
  errorMessage: string | null;
  internalNote: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface PagedResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

// ── Create/update payload types ───────────────────────────────────────────────

export interface AiFeatureConfigCreatePayload {
  clinicId: string;
  featureKey: string;
  enabled?: boolean;
  maxRequestsPerMonth?: number;
  maxTokensPerRequest?: number;
  requireDoctorApprovalForHigh?: boolean;
  requireDoctorApprovalForCritical?: boolean;
  cacheEnabled?: boolean;
  ragEnabled?: boolean;
  safetyLevel?: string;
  reason?: string;
}

export interface AiModelConfigCreatePayload {
  clinicId?: string;
  featureKey: string;
  inferenceProfileArn: string;
  modelId: string;
  temperature: number;
  maxTokens: number;
  fallbackProfileArn?: string;
  reason?: string;
}

export interface AiModelConfigUpdatePayload {
  inferenceProfileArn?: string;
  modelId?: string;
  temperature?: number;
  maxTokens?: number;
  fallbackProfileArn?: string;
  reason?: string;
}

export interface AiPromptTemplateCreatePayload {
  featureKey: string;
  version?: string;
  systemPrompt: string;
  userPrompt: string;
  effectiveFrom?: string;
}

export interface AiMappingCreatePayload {
  type: 'ALLERGY' | 'MEDICINE';
  canonicalName: string;
  drugClassId?: string;
  isHighRisk?: boolean;
  category?: string;
  notes?: string;
  contraindicatedUnderAgeMonths?: number;
  source?: string;
  /** If set, medication name is resolved from medications_master instead of canonicalName */
  medicationMasterId?: string;
}

export interface MedicationMasterSearchResult {
  id: string;
  medicationName: string;
  genericName: string | null;
  strength: string | null;
  dosageForm: string | null;
  manufacturer: string | null;
  contraindications: string | null;
  drugInteractions: string | null;
}

export interface AiMappingUpdatePayload {
  drugClassId?: string;
  isHighRisk?: boolean;
  category?: string;
  notes?: string;
  contraindicatedUnderAgeMonths?: number;
  maxDailyDoseMg?: number;
  source?: string;
  reason?: string;
}

export interface AiUsageQuotaUpdatePayload {
  quotaLimit: number;
  reason: string;
}

export interface ModelTestResult {
  modelId: string;
  inferenceProfileArn: string;
  success: boolean;
  latencyMs: number | null;
  statusMessage: string;
  errorMessage: string | null;
}

export interface PhiRevealResult {
  logId: string;
  patientId: string;
  prescriptionId: string | null;
  appointmentId: string | null;
  requestingUserId: string;
  clinicId: string;
  featureKey: string;
  createdAt: string;
  revealedBy: string;
  revealedAt: string;
  revealReason: string;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export const adminAiReviewModule = {

  getSummary: () =>
    fetchApi<AiReviewSummary>(`${BASE}/summary`),

  // ── Request logs
  getRequestLogs: (params: {
    page?: number; size?: number;
    finalRisk?: string; featureKey?: string;
    blockedByRule?: boolean; hasError?: boolean;
  }) => {
    const qs = new URLSearchParams();
    if (params.page !== undefined) qs.set('page', String(params.page));
    if (params.size !== undefined) qs.set('size', String(params.size));
    if (params.finalRisk)      qs.set('finalRisk', params.finalRisk);
    if (params.featureKey)     qs.set('featureKey', params.featureKey);
    if (params.blockedByRule)  qs.set('blockedByRule', 'true');
    if (params.hasError)       qs.set('hasError', 'true');
    return fetchApi<PagedResponse<AiRequestLogRow>>(`${BASE}/request-logs?${qs}`);
  },

  updateRequestNote: (id: string, note: string) =>
    fetchApi<AiRequestLogRow>(`${BASE}/request-logs/${id}/note`, {
      method: 'PATCH',
      body: JSON.stringify({ note }),
    }),

  markLogInvestigation: (id: string, note: string) =>
    fetchApi<AiRequestLogRow>(`${BASE}/request-logs/${id}/mark-investigation`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  retryAiRequest: (id: string, reason: string) =>
    fetchApi<AiRequestLogRow>(`${BASE}/request-logs/${id}/retry`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  revealPhi: (id: string, reason: string) =>
    fetchApi<PhiRevealResult>(`${BASE}/request-logs/${id}/reveal-phi`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // ── Feature configs
  getFeatureConfigs: (page = 0, size = 50) =>
    fetchApi<PagedResponse<AiFeatureConfigRow>>(`${BASE}/feature-configs?page=${page}&size=${size}`),

  createFeatureConfig: (data: AiFeatureConfigCreatePayload) =>
    fetchApi<AiFeatureConfigRow>(`${BASE}/feature-configs`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateFeatureConfig: (id: string, data: Partial<AiFeatureConfigRow>) =>
    fetchApi<AiFeatureConfigRow>(`${BASE}/feature-configs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  enableFeatureConfig: (id: string) =>
    fetchApi<AiFeatureConfigRow>(`${BASE}/feature-configs/${id}/enable`, { method: 'POST' }),

  disableFeatureConfig: (id: string, reason: string) =>
    fetchApi<AiFeatureConfigRow>(`${BASE}/feature-configs/${id}/disable`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // ── Model configs
  getModelConfigs: (page = 0, size = 50) =>
    fetchApi<PagedResponse<AiModelConfigRow>>(`${BASE}/model-configs?page=${page}&size=${size}`),

  createModelConfig: (data: AiModelConfigCreatePayload) =>
    fetchApi<AiModelConfigRow>(`${BASE}/model-configs`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateModelConfig: (id: string, data: AiModelConfigUpdatePayload) =>
    fetchApi<AiModelConfigRow>(`${BASE}/model-configs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  enableModelConfig: (id: string) =>
    fetchApi<AiModelConfigRow>(`${BASE}/model-configs/${id}/enable`, { method: 'POST' }),

  disableModelConfig: (id: string, reason: string) =>
    fetchApi<AiModelConfigRow>(`${BASE}/model-configs/${id}/disable`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  testModelConnection: (id: string) =>
    fetchApi<ModelTestResult>(`${BASE}/model-configs/${id}/test`, { method: 'POST' }),

  // ── Prompt templates
  getPromptTemplates: (page = 0, size = 20) =>
    fetchApi<PagedResponse<AiPromptTemplateRow>>(`${BASE}/prompt-templates?page=${page}&size=${size}`),

  createPromptTemplate: (data: AiPromptTemplateCreatePayload) =>
    fetchApi<AiPromptTemplateRow>(`${BASE}/prompt-templates`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  createPromptVersion: (id: string, data: AiPromptTemplateCreatePayload) =>
    fetchApi<AiPromptTemplateRow>(`${BASE}/prompt-templates/${id}/create-version`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  activatePromptTemplate: (id: string, note: string) =>
    fetchApi<AiPromptTemplateRow>(`${BASE}/prompt-templates/${id}/activate`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  deactivatePromptTemplate: (id: string, reason: string) =>
    fetchApi<AiPromptTemplateRow>(`${BASE}/prompt-templates/${id}/deactivate`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // ── Usage summary
  getUsageSummary: (page = 0, size = 20) =>
    fetchApi<PagedResponse<AiUsageSummaryRow>>(`${BASE}/usage-summary?page=${page}&size=${size}`),

  updateUsageQuota: (id: string, data: AiUsageQuotaUpdatePayload) =>
    fetchApi<AiUsageSummaryRow>(`${BASE}/usage-summary/${id}/quota`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // ── Doctor approvals
  getDoctorApprovals: (status?: string, page = 0, size = 20) => {
    const qs = new URLSearchParams({ page: String(page), size: String(size) });
    if (status) qs.set('status', status);
    return fetchApi<PagedResponse<AiDoctorApprovalRow>>(`${BASE}/doctor-approvals?${qs}`);
  },

  markApprovalStale: (id: string, reason: string) =>
    fetchApi<AiDoctorApprovalRow>(`${BASE}/doctor-approvals/${id}/mark-stale`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // ── Mappings
  searchMedicationMaster: (q?: string, size = 20) => {
    const qs = new URLSearchParams({ size: String(size) });
    if (q && q.trim()) qs.set('q', q.trim());
    return fetchApi<MedicationMasterSearchResult[]>(`${BASE}/medications-master/search?${qs}`);
  },

  getMappings: (type: AiMappingRow['mappingType'], reviewStatus?: string, page = 0, size = 20) => {
    const qs = new URLSearchParams({ type, page: String(page), size: String(size) });
    if (reviewStatus) qs.set('reviewStatus', reviewStatus);
    return fetchApi<PagedResponse<AiMappingRow>>(`${BASE}/mappings?${qs}`);
  },

  reviewMapping: (id: string, type: AiMappingRow['mappingType'], status: string, notes: string) =>
    fetchApi<AiMappingRow>(`${BASE}/mappings/${id}/review?type=${type}`, {
      method: 'POST',
      body: JSON.stringify({ status, notes }),
    }),

  addMapping: (data: AiMappingCreatePayload) =>
    fetchApi<AiMappingRow>(`${BASE}/mappings`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateMapping: (id: string, type: AiMappingRow['mappingType'], data: AiMappingUpdatePayload) =>
    fetchApi<AiMappingRow>(`${BASE}/mappings/${id}?type=${type}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  approveMapping: (id: string, type: AiMappingRow['mappingType'], reason: string) =>
    fetchApi<AiMappingRow>(`${BASE}/mappings/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ type, reason }),
    }),

  rejectMapping: (id: string, type: AiMappingRow['mappingType'], reason: string) =>
    fetchApi<AiMappingRow>(`${BASE}/mappings/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ type, reason }),
    }),

  bulkApproveMapping: (ids: string[], type: AiMappingRow['mappingType'], reason: string) =>
    fetchApi<number>(`${BASE}/mappings/bulk-approve`, {
      method: 'POST',
      body: JSON.stringify({ ids, type, reason }),
    }),

  bulkRejectMapping: (ids: string[], type: AiMappingRow['mappingType'], reason: string) =>
    fetchApi<number>(`${BASE}/mappings/bulk-reject`, {
      method: 'POST',
      body: JSON.stringify({ ids, type, reason }),
    }),

  // ── Interaction ingestion (openFDA sync) — lives under /admin/medications, not /admin/ai-safety-review
  triggerInteractionSync: () =>
    fetchApi<IngestionRunRow>(`/api/admin/medications/sync-interactions`, { method: 'POST' }),

  getSyncRunStatus: (runId: string) =>
    fetchApi<IngestionRunRow>(`/api/admin/medications/sync-interactions/${runId}`),

  listSyncRuns: (page = 0, size = 20) =>
    fetchApi<PagedResponse<IngestionRunRow>>(`/api/admin/medications/sync-interactions?page=${page}&size=${size}`),

  revertSyncRun: (runId: string) =>
    fetchApi<number>(`/api/admin/medications/sync-interactions/${runId}/revert`, { method: 'POST' }),

  // ── Error events
  getErrorEvents: (page = 0, size = 20) =>
    fetchApi<PagedResponse<AiErrorEventRow>>(`${BASE}/error-events?page=${page}&size=${size}`),

  resolveErrorEvent: (id: string, note: string) =>
    fetchApi<AiErrorEventRow>(`${BASE}/error-events/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  addErrorNote: (id: string, note: string) =>
    fetchApi<AiErrorEventRow>(`${BASE}/error-events/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
};

// Named alias so components can import { adminAiReview }
export const adminAiReview = adminAiReviewModule;
